// istanbul ignore file
import React, { FC } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Router } from '@reach/router';
import { generateTheme } from '@clds/theme-helper';
import light from '@clds/themes';
import { globalTheme } from '@clds/component-theme';
import MainGlobalStyle from '@cld/global-style';
import { LanguageProvider } from '@cld/intl';
import { AnalyticsProvider, useAnalyticsTrack, AnalyticsPlatformProvider } from './common';

import { loadMessages } from './i18n';
import { StandaloneCookbookData } from './data/StandaloneCookbookData';
import { DetailsPage } from './pages/details/DetailsPage';
import { ListPage } from './pages/list/ListPage';
import { trackEventEx } from './utils/trackEvent';

export interface StandaloneCookbookProps {
  initialData: StandaloneCookbookData;
}

/* Global styles needs to be added because we are using internal HTML TEMPLATE */
/* for every other app console delivers global styles - there we have to do it manually */
const ExtraGlobalStyles = createGlobalStyle`
  html {
    font-size: initial;
  }

  .btn-default:visited {
    color: ${globalTheme.palette.surface};
  }

  a:hover, a:focus {
    color: ${globalTheme.palette.primary};
    text-decoration: underline;
  }

  #wrapper {
    overflow: inherit;
  }
`;

const Wrapper = styled.div`
  label {
    margin: 0;
    font-weight: inherit;
  }

  input {
    margin: 0;
  }

  input:focus {
    outline: none;
  }

  pre {
    font-weight: inherit;
  }

  a {
    font-family: inherit;
  }
`;

const StylesProvider: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Wrapper>
    <MainGlobalStyle />
    <ExtraGlobalStyles />
    {children}
  </Wrapper>
);

export const App: FC<React.PropsWithChildren<StandaloneCookbookProps>> = ({ initialData }) => {
  useAnalyticsTrack(trackEventEx);

  return (
    <LanguageProvider loadMessages={loadMessages}>
      <ThemeProvider theme={generateTheme(light)}>
        <AnalyticsPlatformProvider value="website">
          <AnalyticsProvider value="cookbook grid">
            <StylesProvider>
              <Router>
                <ListPage path="cookbook" initialData={initialData} />
                <DetailsPage path="cookbook/:slug" initialRecipe={initialData.displayedRecipe} />
              </Router>
            </StylesProvider>
          </AnalyticsProvider>
        </AnalyticsPlatformProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
};
